import type { BoardItemListItemResponse } from "@/client";
import { dummyCreatorByHandle } from "../discovery/utils/dummyCreators";

/** Keep in sync with App\Services\AskPage\AskPageViewerStatus. */
export type AskPageViewerStatus =
	| "guest"
	| "owner"
	| "none"
	| "free_subscriber"
	| "pass"
	| "member";

export type AskPageBoardClip = {
	id: string;
	url: string | null;
	duration: number;
	title: string | null;
	view_count: number | null;
};

export type AskPageBoard = {
	id: string;
	title: string;
	clips: AskPageBoardClip[];
	/** Full board clip list for the profile play panel (from pageVars, no fetch on open). */
	playback_clips?: BoardItemListItemResponse[];
	clip_count: number;
	icon?: string | null;
};

export type AskPageChannel = {
	platform: string;
	handle: string;
	follower_count: number | null;
	profile_url: string | null;
};

export type AskPagePrice = {
	type: "pass_24h" | "pass_7d" | "subscription";
	amount_minor: number;
	currency: string;
	subscription_name: string | null;
};

export type AskPageVars = {
	influencer_id: string;
	influencer_handle: string;
	influencer_display_name: string;
	influencer_avatar_url: string | null;
	influencer_bio: string | null;
	influencer_country: string | null;
	banner_url: string | null;
	/** 5:1 crop for the web page; null on older payloads, so fall back to banner_url. */
	banner_web_url?: string | null;
	starter_questions_shown: string[];
	starter_questions_pool: string[];
	is_paid_ask_page: boolean;
	/** Visitor↔creator relationship. Join/ask chrome derives from this. */
	status: AskPageViewerStatus;
	entitlement_type: AskPagePrice["type"] | null;
	isSignedIn: boolean;
	/** Signed in only: whether the visitor's own credits pay for what they ask here. */
	paysForOwnAskPageQuestions?: boolean;
	prices: AskPagePrice[];
	/** False once the creator switches every source off: nobody can ask or buy. */
	has_enabled_source: boolean;
	boards: AskPageBoard[];
	social_channels: AskPageChannel[];
	videoCount: number;
	userName?: string;
	user_handle?: string | null;
	user_avatar?: string | null;
};

declare const pageVars: Partial<AskPageVars> & {
	influencer_handle: string;
	influencer_display_name: string;
};

const threadPageDefaults: Pick<
	AskPageVars,
	| "starter_questions_shown"
	| "starter_questions_pool"
	| "is_paid_ask_page"
	| "status"
	| "entitlement_type"
	| "prices"
	| "has_enabled_source"
	| "boards"
	| "social_channels"
	| "influencer_country"
	| "videoCount"
> = {
	starter_questions_shown: [],
	starter_questions_pool: [],
	is_paid_ask_page: false,
	status: "guest",
	entitlement_type: null,
	prices: [],
	// Thread pages don't send it; their ask endpoint refuses on its own.
	has_enabled_source: true,
	boards: [],
	social_channels: [],
	influencer_country: null,
	videoCount: 0,
};

export const askPageVars = (): AskPageVars => {
	const merged = {
		...threadPageDefaults,
		...pageVars,
		influencer_avatar_url: pageVars.influencer_avatar_url ?? null,
		influencer_bio: pageVars.influencer_bio ?? null,
		banner_url: pageVars.banner_url ?? null,
		banner_web_url: pageVars.banner_web_url ?? null,
		isSignedIn: pageVars.isSignedIn ?? false,
		status: pageVars.status ?? (pageVars.isSignedIn ? "none" : "guest"),
	} as AskPageVars;

	if (merged.influencer_id) {
		return merged;
	}
	const dummy = dummyCreatorByHandle(merged.influencer_handle);
	if (!dummy) {
		return merged;
	}
	return {
		...merged,
		influencer_id: `dummy-${dummy.handle}`,
		influencer_display_name: dummy.name,
		influencer_avatar_url: dummy.avatarUrl,
		influencer_bio: dummy.bio,
		banner_url: dummy.coverUrl,
	};
};
